import React, { useEffect, useState } from "react";
import { Box, CircularProgress, useMediaQuery } from "@mui/material";
import { ReactComponent as BackIcon } from "../../assets/svg/BackIcon.svg";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { getApiData } from "../../utils/APIHelper";
import { useNavigate } from "react-router-dom";
import authActions from "../../redux/reducers/auth/actions";
import { ReactComponent as RenoWhiteLogo } from "../../assets/svg/RenoWhiteLogo.svg";
import { store } from "../../redux/store/configureStore";
import { useTheme } from "@emotion/react";

const RoleSelection = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { setUserData, setToken } = authActions;

  const [btnLoad, setBtnLoad] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [locationData, setLocationData] = useState({});

  const { userData } = useSelector((state) => state.auth);
  const roleOptions = [
    {
      label: "I am a designer",
      value: "designer",
      subText: "Creating stunning interiors that elevate living spaces.",
    },
    {
      label: "I am a contractor",
      value: "contractor",
      subText:
        "Crafting dreams with precision, ensuring every detail is perfected.",
    },
    {
      label: "I am a supplier",
      value: "supplier",
      subText:
        "Provide exceptional materials and creative solutions that enhances spaces.",
    },
  ];

  // Helper function to check if a role is non-standard (doesn't require profile creation)
  const isNonStandardRole = (role) => {
    if (!role) return false;
    return !roleOptions.find((opt) => opt.value === role);
  };

  useEffect(() => {
    if (userData?.role) {
      const matched = roleOptions.find(
        (option) => option.value === userData.role
      );
      if (matched) {
        setSelectedRole(matched.value);
      } else {
        // If user already has a role that's not in the standard list (e.g., contractor_project_manager),
        // allow them to proceed without requiring role selection
        // The role is already set, so we can skip the selection
        setSelectedRole(userData.role);
      }
    }
  }, [userData?.role]);

  // this function for to get location detail
  useEffect(() => {
    fetch("https://ipapi.co/json/")
      .then((response) => response.json())
      .then((data) => setLocationData(data))
      .catch((error) => console.error(error));
  }, []);

  useEffect(() => {
    console.log("userDa", userData);

    // If user already has a non-standard role (like contractor_project_manager),
    // automatically navigate them away from role selection since they already have a valid role
    // Non-standard roles don't need profile creation, they go directly to dashboard
    if (userData?.role && isNonStandardRole(userData.role)) {
      // User has a valid non-standard role, skip role selection and profile creation
      if (userData?.is_email_verified === false) {
        navigate("/otp-verify", { state: { data: userData, type: "email" } });
      } else {
        // Non-standard roles skip profile creation and go directly to dashboard
        navigate("/dashboard");
      }
    }
  }, [userData, navigate]);

  async function updateUser() {
    if (selectedRole === "") {
      toast.error("Please select business type");
      return;
    }

    // If user already has a non-standard role (like contractor_project_manager),
    // and they're trying to proceed, skip the API call and navigate directly
    // Non-standard roles don't need profile creation
    if (isNonStandardRole(userData?.role) && selectedRole === userData.role) {
      // User already has a valid non-standard role, proceed without updating
      // Skip profile creation for non-standard roles
      if (userData?.is_email_verified === false) {
        navigate("/otp-verify", { state: { data: userData, type: "email" } });
      } else {
        // Non-standard roles go directly to dashboard, no profile creation needed
        navigate("/dashboard");
      }
      return;
    }

    setBtnLoad(true);
    const address = `${locationData?.city ? locationData?.city + "," : ""} ${
      locationData?.region ? locationData?.region + "," : ""
    } ${locationData?.country_name || ""}`;

    let data = {
      id: userData?.id,
      email: userData?.email,
      role: selectedRole,
      device_name: locationData?.ip || "",
      login_address: address,
    };
    console.log("data", data);
    try {
      const response = await getApiData(
        `api/contractor/update-role`,
        "PUT",
        data
      );
      if (response.success) {
        // toast.success(response?.message || "");
        if (
          response?.is_email_verified === false ||
          response?.data?.is_email_verified === false
        ) {
          navigate("/otp-verify", { state: { data, type: "email" } });
          dispatch(setUserData(response?.data));
        } else {
          dispatch(setToken(response?.token));
          dispatch(setUserData(response?.data));
          navigate("/create-profile");
        }
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(error.toString());
    } finally {
      setBtnLoad(false);
    }
  }

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      {!isMobile && (
        <Box
          n
          sx={{
            position: "relative",
            flex: 1.8,
            backgroundImage: `
      linear-gradient(180deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.12) 32.45%),
      linear-gradient(180deg, rgba(0, 0, 0, 0) 72.71%, rgba(0, 0, 0, 0.6) 86.96%),
      url(/assets/images/bg5.jpg)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              bottom: 50,
              left: 70,
            }}
          >
            <RenoWhiteLogo width={170} height="auto" />
          </Box>
        </Box>
      )}

      <Box
        sx={{
          flex: 2,
          padding: "2vh 4vw",
          display: "flex",
          flexDirection: "column",
          justifyContent: isMobile ? "flex-start" : "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "15px",
            marginBottom: "3vh",
          }}
        >
          <BackIcon
            onClick={() => {
              store.dispatch(authActions.clearAllData());
              navigate("/login");
            }}
            style={{ cursor: "pointer" }}
          />

          <span
            style={{
              fontFamily: "Inter Tight",
              color: "#0D0D0E",
              fontSize: "18px",
              fontWeight: 500,
            }}
          >
            Back
          </span>
        </Box>
        <div
          style={{
            width: "100%",
            fontSize: isMobile ? "24px" : "40px",
            fontFamily: "Inter Tight",
            textAlign: "left",
            fontWeight: "600",
            color: "#000000",
            letterSpacing: "-0.4px",
            marginBottom: "3vh",
          }}
        >
          What type of business are you?
        </div>

        {roleOptions.map(({ label, value, subText }) => (
          <Box
            key={value}
            onClick={() => setSelectedRole(value)}
            sx={{
              margin: "10px 0",
              padding: "18px",
              border: "1px solid",
              borderRadius: "8px",
              borderColor: selectedRole === value ? "#3f51b5" : "#D7D8D9",
              backgroundColor:
                selectedRole === value ? "#f0f4ff" : "transparent",
              cursor: "pointer",
              transition: "background-color 0.3s, border-color 0.3s",
              "&:hover": {
                backgroundColor: "#f0f4ff",
              },
            }}
          >
            <p
              style={{
                color: "#0D0D0E",
                fontSize: "16px",
                fontWeight: "500",
                fontFamily: "Inter Tight",
                marginBottom: ".5vh",
              }}
            >
              {label}
            </p>
            <p
              style={{
                color: "#949DAA",
                fontSize: "14px",
                fontWeight: "500",
                fontFamily: "Inter Tight",
              }}
            >
              {subText}
            </p>
          </Box>
        ))}
        <button
          style={{
            marginTop: "3vh",
            width: "fit-content",
            padding: "1.5vh 4vw",
            backgroundColor: "#4D6BF6",
            borderRadius: "12px",
            fontSize: "16px",
            fontWeight: "600",
            color: "#fff",
            cursor: "pointer",
            border: "1px solid transparent",
            fontFamily: "Inter Tight",
            textAlign: "center",
          }}
          onClick={updateUser}
          disabled={btnLoad}
        >
          {btnLoad ? (
            <CircularProgress size={24} sx={{ color: "#fff" }} />
          ) : (
            "Next step"
          )}
        </button>
      </Box>
    </Box>
  );
};

export default RoleSelection;
